import type { Point, Circle, Polygon, DrawingStyle, CanvasPoint, CanvasLine } from '@/types/canvas';
import type { MediaPipeLandmark } from '@/types/mediapipe';
import { POSE_CONNECTIONS } from '@/types/mediapipe';
import { CANVAS_CONFIG } from '@/utils/constants';

/**
 * Clear canvas
 */
export function clearCanvas(ctx: CanvasRenderingContext2D) {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}

/**
 * Draw a point on canvas
 */
export function drawPoint(
  ctx: CanvasRenderingContext2D,
  point: CanvasPoint
) {
  ctx.beginPath();
  ctx.arc(
    point.x,
    point.y,
    point.radius || CANVAS_CONFIG.LANDMARK_RADIUS,
    0,
    2 * Math.PI
  );
  ctx.fillStyle = point.color || '#ffffff';
  ctx.fill();
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 1;
  ctx.stroke();
}

/**
 * Draw a line on canvas
 */
export function drawLine(
  ctx: CanvasRenderingContext2D,
  line: CanvasLine
) {
  ctx.beginPath();
  ctx.moveTo(line.start.x, line.start.y);
  ctx.lineTo(line.end.x, line.end.y);
  ctx.strokeStyle = line.color || '#ffffff';
  ctx.lineWidth = line.width || CANVAS_CONFIG.CONNECTION_WIDTH;
  ctx.stroke();
}

/**
 * Draw a circle on canvas
 */
export function drawCircle(
  ctx: CanvasRenderingContext2D,
  circle: Circle,
  style: DrawingStyle
) {
  ctx.beginPath();
  ctx.arc(circle.center.x, circle.center.y, circle.radius, 0, 2 * Math.PI);
  ctx.strokeStyle = style.strokeColor;
  ctx.lineWidth = style.lineWidth;
  ctx.stroke();

  if (style.fillAlpha) {
    ctx.fillStyle = style.fillColor.replace(')', `, ${style.fillAlpha})`).replace('rgb', 'rgba');
    ctx.fill();
  }
}

/**
 * Draw a polygon on canvas
 */
export function drawPolygon(
  ctx: CanvasRenderingContext2D,
  polygon: Polygon,
  style: DrawingStyle
) {
  if (polygon.points.length < 2) return;

  ctx.beginPath();
  ctx.moveTo(polygon.points[0].x, polygon.points[0].y);

  for (let i = 1; i < polygon.points.length; i++) {
    ctx.lineTo(polygon.points[i].x, polygon.points[i].y);
  }

  ctx.closePath();
  ctx.strokeStyle = style.strokeColor;
  ctx.lineWidth = style.lineWidth;
  ctx.stroke();

  if (style.fillAlpha) {
    ctx.fillStyle = style.fillColor.replace(')', `, ${style.fillAlpha})`).replace('rgb', 'rgba');
    ctx.fill();
  }
}

/**
 * Return true when a landmark's coordinates are valid and usable for drawing.
 *
 * A landmark is considered INVALID (should be skipped) when ANY of the
 * following is true:
 *   1. The x or y value is NaN or ±Infinity.
 *   2. Both x and y are at or below 0.001 (the MediaPipe missing-landmark
 *      sentinel — undetected keypoints are pinned to ~(0,0)).
 *   3. Either coordinate is outside the normalised range with a small margin
 *      (below -0.05 or above 1.05), indicating garbage data.
 *   4. Visibility is explicitly provided AND is below 0.5 (low-confidence).
 *
 * Note: when visibility is UNDEFINED the guard does NOT reject the landmark —
 * some data sources omit the field for valid, well-placed keypoints.
 */
function isLandmarkDrawable(landmark: MediaPipeLandmark): boolean {
  const { x, y, visibility } = landmark;
  // Rule 1: reject NaN / Infinity
  if (!isFinite(x) || !isFinite(y)) return false;
  // Rule 2: reject (0,0) sentinel — missing keypoints are pinned near the origin
  if (x <= 0.001 && y <= 0.001) return false;
  // Rule 3: reject out-of-range coordinates (with a small 5% margin for rounding)
  if (x < -0.05 || x > 1.05 || y < -0.05 || y > 1.05) return false;
  // Rule 4: reject explicitly low visibility
  if (visibility !== undefined && visibility < 0.5) return false;
  return true;
}

/**
 * Draw pose landmarks
 */
export function drawPoseLandmarks(
  ctx: CanvasRenderingContext2D,
  landmarks: MediaPipeLandmark[],
  canvasWidth: number,
  canvasHeight: number,
  color = '#00ff00'
) {
  landmarks.forEach((landmark) => {
    if (!isLandmarkDrawable(landmark)) return;

    drawPoint(ctx, {
      x: landmark.x * canvasWidth,
      y: landmark.y * canvasHeight,
      color,
      radius: CANVAS_CONFIG.LANDMARK_RADIUS,
    });
  });
}

/**
 * Draw pose connections (skeleton)
 */
export function drawPoseConnections(
  ctx: CanvasRenderingContext2D,
  landmarks: MediaPipeLandmark[],
  canvasWidth: number,
  canvasHeight: number,
  color = '#00ff00'
) {
  POSE_CONNECTIONS.forEach(([startIdx, endIdx]) => {
    const start = landmarks[startIdx];
    const end = landmarks[endIdx];

    if (!start || !end) return;
    if (!isLandmarkDrawable(start) || !isLandmarkDrawable(end)) return;

    drawLine(ctx, {
      start: {
        x: start.x * canvasWidth,
        y: start.y * canvasHeight,
      },
      end: {
        x: end.x * canvasWidth,
        y: end.y * canvasHeight,
      },
      color,
      width: CANVAS_CONFIG.CONNECTION_WIDTH,
    });
  });
}

/**
 * Draw text label on canvas
 */
export function drawLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  position: Point,
  options: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    padding?: number;
  } = {}
) {
  const {
    fontSize = 12,
    color = '#ffffff',
    backgroundColor = '#000000',
    padding = 4,
  } = options;

  ctx.font = `${fontSize}px sans-serif`;
  const metrics = ctx.measureText(text);
  const width = metrics.width + padding * 2;
  const height = fontSize + padding * 2;

  // Draw background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(position.x, position.y - height, width, height);

  // Draw text
  ctx.fillStyle = color;
  ctx.fillText(text, position.x + padding, position.y - padding);
}

/**
 * Get distance between two points
 */
export function getDistance(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Check if point is inside circle
 */
export function isPointInCircle(point: Point, circle: Circle): boolean {
  return getDistance(point, circle.center) <= circle.radius;
}

/**
 * Check if point is inside polygon
 */
export function isPointInPolygon(point: Point, polygon: Polygon): boolean {
  let inside = false;
  const points = polygon.points;

  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
}
